import { ApplicationRef, DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, take, timer } from 'rxjs';
import { BeforeInstallPromptChoiceResult, BeforeInstallPromptEvent } from '../models/before-install-prompt-event';
import { clearPwaInstallDismissal, readPwaInstallDismissedUntil, writePwaInstallDismissal, } from '../utils/pwa-install-storage';

const INSTALL_BANNER_DELAY_MS = 1200;
type IosNavigator = Navigator & { standalone?: boolean };

@Injectable({
  providedIn: 'root',
})
export class PwaInstallService {
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  private readonly browserWindow = this.isBrowser ? window : null;
  private readonly browserNavigator = this.isBrowser ? navigator as IosNavigator : null;
  private readonly browserStorage = this.browserWindow?.localStorage ?? null;
  private readonly displayModeMediaQuery = this.browserWindow?.matchMedia('(display-mode: standalone)') ?? null;

  private readonly deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  private readonly dismissedUntil = signal<number | null>(null);
  private readonly standalone = signal(this.detectStandaloneMode());
  private readonly installedSinceLaunch = signal(false);
  private readonly displayReady = signal(false);
  private readonly promptInProgress = signal(false);

  private dismissalTimeoutId: number | null = null;

  readonly isIos = this.detectIos();
  readonly supportsNativePrompt = this.detectNativePromptSupport();
  readonly isInstalled = computed(() => this.standalone() || this.installedSinceLaunch());
  readonly isPromptInProgress = this.promptInProgress.asReadonly();
  readonly canUseNativePrompt = computed(() => this.deferredPrompt() !== null && !this.isInstalled());
  readonly canShowIosGuide = computed(() => this.isIos && !this.isInstalled());
  readonly isDismissed = computed(() => this.dismissedUntil() !== null);
  readonly canSuggestInstallation = computed(() =>
    !this.isInstalled() &&
    !this.isDismissed() &&
    (this.canUseNativePrompt() || this.canShowIosGuide())
  );
  readonly isInstallSuggestionVisible = computed(() => this.displayReady() && this.canSuggestInstallation());

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    this.syncDismissalState();
    this.bindWindowEvents();
    this.waitForDisplayWindow();
  }

  dismissSuggestion(): void {
    if (!this.browserStorage) return;

    const dismissedUntil = writePwaInstallDismissal(this.browserStorage);
    this.dismissedUntil.set(dismissedUntil);
    this.scheduleDismissalReset(dismissedUntil);
  }

  async promptInstall(): Promise<BeforeInstallPromptChoiceResult | null> {
    const promptEvent = this.deferredPrompt();
    if (!promptEvent || this.isIos || this.isInstalled()) {
      return null;
    }

    this.promptInProgress.set(true);

    try {
      await promptEvent.prompt();
      const result = await promptEvent.userChoice;

      this.deferredPrompt.set(null);

      if (result.outcome === 'dismissed') {
        this.dismissSuggestion();
      }

      return result;
    } finally {
      this.promptInProgress.set(false);
    }
  }

  // Delays banner visibility until the app is stable and the first paint has settled.
  private waitForDisplayWindow(): void {
    this.appRef.isStable.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => timer(INSTALL_BANNER_DELAY_MS)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.syncDismissalState();
      this.displayReady.set(true);
    });
  }

  // Subscribes to browser install lifecycle events and keeps local state in sync.
  private bindWindowEvents(): void {
    const beforeInstallPromptHandler = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      promptEvent.preventDefault();
      this.syncDismissalState();
      this.deferredPrompt.set(promptEvent);
    };

    const appInstalledHandler = () => {
      this.markAsInstalled();
      this.deferredPrompt.set(null);
      this.clearDismissalState();
    };

    const displayModeChangeHandler = () => {
      this.refreshStandaloneState();
    };

    this.browserWindow?.addEventListener('beforeinstallprompt', beforeInstallPromptHandler as EventListener);
    this.browserWindow?.addEventListener('appinstalled', appInstalledHandler);
    this.displayModeMediaQuery?.addEventListener('change', displayModeChangeHandler);

    this.destroyRef.onDestroy(() => {
      this.browserWindow?.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler as EventListener);
      this.browserWindow?.removeEventListener('appinstalled', appInstalledHandler);
      this.displayModeMediaQuery?.removeEventListener('change', displayModeChangeHandler);
      this.clearDismissalTimer();
    });
  }

  // Restores the persisted dismissal window and clears it automatically when expired.
  private syncDismissalState(): void {
    if (!this.browserStorage) return;

    const dismissedUntil = readPwaInstallDismissedUntil(this.browserStorage);
    if (!dismissedUntil) {
      this.dismissedUntil.set(null);
      this.clearDismissalTimer();
      return;
    }

    if (dismissedUntil <= Date.now()) {
      this.clearDismissalState();
      return;
    }

    this.dismissedUntil.set(dismissedUntil);
    this.scheduleDismissalReset(dismissedUntil);
  }

  // Removes the stored dismissal so the banner can be shown again.
  private clearDismissalState(): void {
    if (!this.browserStorage) return;

    clearPwaInstallDismissal(this.browserStorage);
    this.dismissedUntil.set(null);
    this.clearDismissalTimer();
  }

  // Schedules the local dismissal flag to expire without requiring a reload.
  private scheduleDismissalReset(dismissedUntil: number): void {
    const remainingMs = dismissedUntil - Date.now();
    if (remainingMs <= 0) {
      this.clearDismissalState();
      return;
    }

    this.clearDismissalTimer();
    this.dismissalTimeoutId = window.setTimeout(() => {
      this.clearDismissalState();
    }, remainingMs);
  }

  // Cancels any pending dismissal expiration timer before a new one is created.
  private clearDismissalTimer(): void {
    if (this.dismissalTimeoutId === null || !this.browserWindow) {
      return;
    }

    this.browserWindow.clearTimeout(this.dismissalTimeoutId);
    this.dismissalTimeoutId = null;
  }

  // Checks whether the app is already running in standalone display mode.
  private detectStandaloneMode(): boolean {
    if (!this.browserWindow) return false;

    return this.browserWindow.matchMedia('(display-mode: standalone)').matches || this.browserNavigator?.standalone === true;
  }

  // Detects iOS and iPadOS devices, including iPad desktop-mode user agents.
  private detectIos(): boolean {
    if (!this.browserWindow || !this.browserNavigator) return false;

    const userAgent = this.browserNavigator.userAgent;
    const isIosDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isIpadOsDesktopMode = this.browserNavigator.platform === 'MacIntel' && this.browserNavigator.maxTouchPoints > 1;

    return isIosDevice || isIpadOsDesktopMode;
  }

  // Detects whether the current browser can expose the native install prompt event.
  private detectNativePromptSupport(): boolean {
    if (!this.browserWindow) return false;

    return 'onbeforeinstallprompt' in this.browserWindow;
  }

  // Re-evaluates standalone mode after browser display-mode changes.
  private refreshStandaloneState(): void {
    const standalone = this.detectStandaloneMode();
    this.standalone.set(standalone);

    if (standalone) {
      this.markAsInstalled();
      this.deferredPrompt.set(null);
    }
  }

  // Marks the app as installed for the current session and hides install affordances.
  private markAsInstalled(): void {
    this.installedSinceLaunch.set(true);
    this.standalone.set(true);
  }
}
